import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, Briefcase, Users, Building2, IdCard,
  MapPin, ShieldCheck, UserCog, ScrollText, UserSquare,
  type LucideIcon,
} from 'lucide-react';
import { usePermission } from '../auth/AuthContext';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** null = always visible to any authenticated user (e.g. Dashboard). */
  requiredPermission: string | null;
  section: 'main' | 'programmes' | 'staff' | 'hr' | 'admin' | 'finance';
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, requiredPermission: null, section: 'main' },
  { label: 'Programmes', to: '/programmes', icon: FolderKanban, requiredPermission: 'programmes.view', section: 'programmes' },
  { label: 'Projects', to: '/projects', icon: Briefcase, requiredPermission: 'projects.view', section: 'programmes' },
  { label: 'My Profile', to: '/staff', icon: UserSquare, requiredPermission: 'employees.view_own', section: 'staff' },
  { label: 'HR', to: '/hr', icon: LayoutDashboard, requiredPermission: 'employees.manage', section: 'hr' },
  { label: 'Finance', to: '/finance', icon: LayoutDashboard, requiredPermission: 'finance.dashboard.view', section: 'finance' },
  { label: 'Users', to: '/admin/users', icon: Users, requiredPermission: 'admin.users.view', section: 'admin' },
  { label: 'Departments', to: '/admin/departments', icon: Building2, requiredPermission: 'admin.departments.manage', section: 'admin' },
  { label: 'Positions', to: '/admin/positions', icon: IdCard, requiredPermission: 'admin.positions.manage', section: 'admin' },
  { label: 'Locations', to: '/admin/locations', icon: MapPin, requiredPermission: 'admin.locations.manage', section: 'admin' },
  { label: 'Roles & Permissions', to: '/admin/roles', icon: ShieldCheck, requiredPermission: 'admin.roles.view', section: 'admin' },
  { label: 'Approval Matrix Health', to: '/admin/approval-matrix-health', icon: ShieldCheck, requiredPermission: 'admin.roles.view', section: 'admin' },
  { label: 'Partner Accounts', to: '/admin/partner-accounts', icon: ShieldCheck, requiredPermission: 'admin.partner_accounts.view', section: 'admin' },
  { label: 'Delegations', to: '/admin/delegations', icon: UserCog, requiredPermission: 'admin.delegations.manage', section: 'admin' },
  { label: 'Audit Trail', to: '/admin/audit', icon: ScrollText, requiredPermission: 'admin.audit.view', section: 'admin' },
];

/**
 * Filters purely for navigation convenience - a user without
 * admin.departments.manage simply doesn't see a Departments link they'd
 * get a 403 from anyway. This list will grow with new top-level
 * entries (Partners, Vendors, Procurement, ...) as those modules ship,
 * without needing any restructuring here.
 */
function NavEntry({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const allowed = item.requiredPermission === null || usePermission(item.requiredPermission);
  if (!allowed) return null;

  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => 'nav-link' + (isActive ? ' nav-link-active' : '')}
      end={item.to === '/'}
      title={collapsed ? item.label : undefined}
    >
      <Icon size={18} strokeWidth={2} />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}

function SidebarSection({ label, items, collapsed }: { label: string; items: NavItem[]; collapsed: boolean }) {
  const visible = items.filter((item) => item.requiredPermission === null || usePermission(item.requiredPermission));
  if (visible.length === 0) return null;

  return (
    <>
      {!collapsed && <div className="sidebar-section-label">{label}</div>}
      {visible.map((item) => <NavEntry key={item.to} item={item} collapsed={collapsed} />)}
    </>
  );
}

export function Sidebar({ collapsed }: { collapsed: boolean }) {
  return (
    <nav className={'sidebar' + (collapsed ? ' sidebar-collapsed' : '')}>
      {navItems.filter((i) => i.section === 'main').map((item) => <NavEntry key={item.to} item={item} collapsed={collapsed} />)}

      <SidebarSection label="Programmes & Projects" items={navItems.filter((i) => i.section === 'programmes')} collapsed={collapsed} />
      <SidebarSection label="My Workspace" items={navItems.filter((i) => i.section === 'staff')} collapsed={collapsed} />
      <SidebarSection label="HR Management" items={navItems.filter((i) => i.section === 'hr')} collapsed={collapsed} />
      <SidebarSection label="Finance" items={navItems.filter((i) => i.section === 'finance')} collapsed={collapsed} />
      <SidebarSection label="Administration" items={navItems.filter((i) => i.section === 'admin')} collapsed={collapsed} />
    </nav>
  );
}
